/**
 * MatrixPicker — scoped picker invoked from the control inspector to
 * choose a CC/channel mapping by browsing the matrix.
 *
 * Differs from the MatrixViewer: this is interactive. Cells are clickable.
 * Free cells are highlighted, claimed cells are dimmed with their claim
 * label visible so the user can SEE clashes-by-intent (e.g. "I'm reusing
 * APC40's Fader 1 on purpose"). Clicking a cell — free or claimed — calls
 * onPick(channel, ccNumber) and closes.
 *
 * Phase 1: CC kind only. Note picker arrives when something in OPButtons
 * actually edits a note mapping (sequencer lane note + channel is the
 * closest candidate but its UI doesn't open this picker yet).
 *
 * Layout matches MatrixViewer's grid for visual consistency. When the
 * matrix is empty, the picker shows the same empty-state message — the
 * user can still cancel out and type values manually.
 */

import { createMemo, For, onCleanup, onMount, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import {
  type MatrixSlot,
  type MatrixSlotKind,
  deviceForClaim,
  primaryClaim,
} from '@opbuttons/schema';
import { noteLabelFor, noteNumberFor } from '../matrix/noteNames.ts';
import { matrixLibrary } from '../matrix/store.ts';
import { barPosition } from '../settings/store.ts';
import { attachAutosize } from './matrixCellAutosize.ts';

interface MatrixPickerProps {
  /** Which namespace to browse. Defaults to 'cc' so every pre-2026-08-10
   *  caller (button / lfo / fader / radial / grid cell, all CC) is unchanged.
   *  Added because the inspector's Send browser can now be pointed at a note
   *  or a program, and its "Open Matrix" was opening a CC grid regardless -
   *  which would have written a ccNumber onto a note button. */
  space?: MatrixSlotKind;
  /** Current selection — highlighted in the grid. -1 channel = none. */
  currentChannel?: number;
  currentCcNumber?: number;
  /** What is being assigned, in the operator's own words ("KICK",
   *  "Grid · cell 7"). Rendered at the top so the grid is not an
   *  anonymous wall of cells - which mattered little when only two kinds
   *  could open this, and matters a lot now that a 16-cell grid can
   *  (2026-07-31). */
  forLabel?: string;
  /** False when the control has no real mapping yet. A fresh control sits
   *  at CC 0 / ch 0, which is indistinguishable from a deliberate CC 0
   *  binding - highlighting it would tell a novice "you are here" about a
   *  cell they never chose. When false, nothing is highlighted and the
   *  header says so. */
  hasCurrent?: boolean;
  /** Fired when the user clicks a cell. The picker closes after firing. */
  onPick: (channel: number, ccNumber: number) => void;
  onClose: () => void;
}

const CHANNELS: number[] = Array.from({ length: 16 }, (_, i) => i + 1);

/** Split a device name at its first whitespace so cells can render the
 *  first word on line 1 and everything after on line 2. Matches
 *  splitDeviceName in MatrixViewer — kept private per file so the two
 *  matrix surfaces stay orthogonal. */
function splitDeviceName(name: string): { first: string; rest: string } {
  const trimmed = name.trim();
  const i = trimmed.search(/\s/);
  if (i < 0) return { first: trimmed, rest: '' };
  return { first: trimmed.slice(0, i), rest: trimmed.slice(i + 1).trim() };
}

/** Default rows shown when the matrix has no claims yet — the user still
 *  needs to be able to pick SOMETHING. Show a sparse but useful spread:
 *  0..31 (common controllers) so the picker isn't an empty void. */
const DEFAULT_ROWS: number[] = Array.from({ length: 32 }, (_, i) => i);

export function MatrixPicker(props: MatrixPickerProps) {
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') props.onClose();
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  const showsCurrent = () =>
    props.hasCurrent !== false
    && typeof props.currentCcNumber === 'number'
    && typeof props.currentChannel === 'number';

  // Scroll the current cell to the top-left of the grid on open (operator
  // ask 2026-07-31: *"the currently used cell should be highlighted and
  // land in the top left of the screen"*). Without this the ring was
  // frequently off-screen - the body scrolls, and the grid is 16 channels
  // wide by 32+ CC rows deep, so "highlighted" was invisible in practice.
  //
  // NOT flush to 0,0: a cell jammed against the sticky header/row-header
  // reads as clipped rather than as "here you are", so it is nudged back by
  // roughly one cell to leave a row and a column of context.
  let currentCellEl: HTMLButtonElement | undefined;
  const SCROLL_PAD_PX = 68;
  onMount(() => {
    if (!showsCurrent()) return;
    // A frame later: the grid has to be laid out before an offset means
    // anything, and `attachAutosize` runs on the same tick.
    requestAnimationFrame(() => {
      const cell = currentCellEl;
      if (!cell) return;
      cell.scrollIntoView({ block: 'start', inline: 'start', behavior: 'auto' });
      const body = cell.closest('.matrix-body');
      if (body) {
        body.scrollTop = Math.max(0, body.scrollTop - SCROLL_PAD_PX);
        body.scrollLeft = Math.max(0, body.scrollLeft - SCROLL_PAD_PX);
      }
    });
  });

  const library = matrixLibrary;
  const space = (): MatrixSlotKind => props.space ?? 'cc';
  const spaceSlots = createMemo<MatrixSlot[]>(() =>
    library().slots.filter((s) => s.kind === space())
  );

  /** One row of the grid. `value` is what gets picked and written to the
   *  item; `label` is what the row header shows. They differ only for notes,
   *  where the Matrix's identity is a LETTER but the item stores a number. */
  interface PickRow { value: number; label: string }

  // Rows = the set of claimed CC numbers, plus the default spread for
  // empty/sparse matrices, plus the currently-selected CC (so the user
  // always sees their own selection on screen).
  //
  // NOTES GET NO DEFAULT SPREAD, for the same reason MatrixViewer gives none:
  // note labels are non-canonical (the +/-12 manufacturer offset), so
  // inventing 32 of them would be inventing rows that may correspond to
  // nothing on the rig. Claimed labels only, plus the current note.
  const rows = createMemo<PickRow[]>(() => {
    if (space() === 'note') {
      const seen = new Map<number, string>();
      for (const sl of spaceSlots()) {
        const label = (sl.noteLabel ?? '').trim();
        if (!label) continue;
        // A label this app cannot parse is real data on the rig but cannot be
        // written into a numeric item field, so it is skipped rather than
        // guessed at.
        const num = noteNumberFor(label);
        if (num !== null && !seen.has(num)) seen.set(num, label);
      }
      if (typeof props.currentCcNumber === 'number' && !seen.has(props.currentCcNumber)) {
        seen.set(props.currentCcNumber, noteLabelFor(props.currentCcNumber));
      }
      return Array.from(seen.entries())
        .sort((x, y) => x[0] - y[0])
        .map(([value, label]) => ({ value, label: `${value} · ${label}` }));
    }
    const set = new Set<number>(DEFAULT_ROWS);
    for (const sl of spaceSlots()) {
      const num = space() === 'pc' ? sl.pcNumber : sl.ccNumber;
      if (typeof num === 'number') set.add(num);
    }
    if (typeof props.currentCcNumber === 'number') set.add(props.currentCcNumber);
    const prefix = space() === 'pc' ? 'PC' : 'CC';
    return Array.from(set)
      .sort((x, y) => x - y)
      .map((value) => ({ value, label: `${prefix} ${value}` }));
  });

  function slotAt(value: number, channel: number): MatrixSlot | undefined {
    return spaceSlots().find((sl) => {
      if (sl.channel !== channel) return false;
      if (space() === 'cc') return sl.ccNumber === value;
      if (space() === 'pc') return sl.pcNumber === value;
      return noteNumberFor((sl.noteLabel ?? '').trim()) === value;
    });
  }

  function pick(channel: number, ccNumber: number): void {
    props.onPick(channel, ccNumber);
    props.onClose();
  }

  return (
    <Portal>
      <div
        class="matrix-overlay"
        onClick={props.onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Pick a CC mapping from the matrix"
      >
        <div class="matrix-modal matrix-modal-full" data-bar-position={barPosition()} onClick={(e) => e.stopPropagation()}>
        <header class="matrix-head">
          <div class="matrix-title">PICK FROM MATRIX</div>
          <button
            type="button"
            class="matrix-close"
            onClick={props.onClose}
            title="Cancel (Esc)"
            aria-label="Cancel"
          >
            ×
          </button>
        </header>

        {/* Context band. OUTSIDE `.matrix-body` on purpose: the body is the
            scroll container, and it now auto-scrolls to the current cell on
            open - which was carrying both of these off-screen, losing the
            "what am I assigning" line and the novice-facing key at exactly
            the moment they are first needed. `.matrix-modal` is a flex
            column, so a `flex: none` band here simply never scrolls. */}
        <div class="matrix-pick-band">
          <Show when={props.forLabel}>
            <div class="matrix-pick-target">
              <span class="matrix-pick-target-verb">Assigning</span>
              <span class="matrix-pick-target-name">{props.forLabel}</span>
              <span class="matrix-pick-target-now">
                {showsCurrent()
                  ? `currently CC ${props.currentCcNumber} · channel ${(props.currentChannel ?? 0) + 1}`
                  : 'not assigned yet'}
              </span>
            </div>
          </Show>
          {/* Reworded 2026-07-31: this used to read "Free cells are
              highlighted", which is backwards - free cells are the plain
              ones; CLAIMED cells carry the device tint. */}
          <div class="matrix-pick-key">
            <span class="matrix-pick-key-item">
              <span class="matrix-pick-key-swatch" data-k="current" />NOW = where this control is
            </span>
            <span class="matrix-pick-key-item">
              <span class="matrix-pick-key-swatch" data-k="claimed" />tinted = already used in your rig
            </span>
            <span class="matrix-pick-key-item">
              <span class="matrix-pick-key-swatch" data-k="free" />plain = free
            </span>
            <span class="matrix-pick-key-hint">Tap any cell to move this control there.</span>
          </div>
        </div>

        <div class="matrix-body">
          <div class="matrix-grid" role="grid">
            <div class="matrix-grid-head" role="row">
              <div class="matrix-grid-corner" />
              <For each={CHANNELS}>
                {(ch) => (
                  <div class="matrix-grid-th" role="columnheader" title={`Channel ${ch}`}>
                    {ch}
                  </div>
                )}
              </For>
            </div>
            <For each={rows()}>
              {(row) => (
                <div class="matrix-grid-row" role="row">
                  <div class="matrix-grid-rh" role="rowheader">{row.label}</div>
                  <For each={CHANNELS}>
                    {(ch) => {
                      const slot = slotAt(row.value, ch);
                      const claim = slot ? primaryClaim(slot) : undefined;
                      const device = claim ? deviceForClaim(library(), claim) : undefined;
                      const shareCount = slot?.claims.length ?? 0;
                      const isCurrent =
                        showsCurrent()
                        && props.currentChannel === ch - 1
                        && props.currentCcNumber === row.value;
                      const tooltip = slot && claim
                        ? `Claimed by ${slot.claims.map((c) => {
                            const d = deviceForClaim(library(), c);
                            return `${d?.name ?? '?'} · ${c.controlLabel}`;
                          }).join(' | ')}. Click to overwrite anyway.`
                        : `Free · ${row.label} ch ${ch}. Click to assign.`;
                      let cellEl: HTMLButtonElement | undefined;
                      let dispose: (() => void) | undefined;
                      onCleanup(() => dispose?.());
                      return (
                        <button
                          type="button"
                          class="matrix-pick-cell"
                          ref={(el) => {
                            cellEl = el;
                            // The scroll-on-open target (see onMount).
                            if (isCurrent) currentCellEl = el;
                            if (slot && cellEl) {
                              dispose?.();
                              dispose = attachAutosize(cellEl);
                            }
                          }}
                          data-claimed={slot ? 'true' : 'false'}
                          data-shared={shareCount > 1 ? 'true' : 'false'}
                          data-current={isCurrent ? 'true' : 'false'}
                          data-hue={device?.color ?? ''}
                          onClick={() => pick(ch - 1, row.value)}
                          title={tooltip}
                        >
                          {slot && claim ? (() => {
                            const parts = splitDeviceName(device?.name ?? '?');
                            return (
                              <>
                                <span class="matrix-grid-cell-label">
                                  <span class="matrix-grid-cell-label-device matrix-grid-cell-label-device-first">
                                    {parts.first}
                                  </span>
                                  <span class="matrix-grid-cell-label-device matrix-grid-cell-label-device-rest">
                                    {parts.rest}
                                  </span>
                                  <span class="matrix-grid-cell-label-control">{claim.controlLabel}</span>
                                </span>
                                <Show when={shareCount > 1}>
                                  <span class="matrix-grid-cell-share">{shareCount}×</span>
                                </Show>
                              </>
                            );
                          })() : null}
                        </button>
                      );
                    }}
                  </For>
                </div>
              )}
            </For>
          </div>

          <Show when={library().devices.length === 0}>
            <div class="matrix-empty-sub" style="margin-top:14px">
              No devices in the matrix yet — claim cells are empty. Any
              selection here just sets CC + channel on this control directly.
            </div>
          </Show>
        </div>
        </div>
      </div>
    </Portal>
  );
}
